import { Link } from "react-router-dom";
import { ArrowLeft, Languages } from "lucide-react";
import { Reveal, PageHero, Eyebrow } from "@/components/Motion";
import { CONTACTS } from "@/data/content";
import { useLanguage } from "@/i18n";

const Section = ({ num, title, children }) => (
  <Reveal>
    <section data-testid={`privacy-section-${num}`} className="border-t border-stone-300 pt-10">
      <div className="flex items-baseline gap-5">
        <span className="font-mono text-xs tracking-[0.25em] text-[#8F6B3C]">{num}</span>
        <h2 className="font-serif text-2xl uppercase tracking-wide text-stone-900 md:text-3xl">
          {title}
        </h2>
      </div>
      <div className="mt-5 space-y-4 text-sm leading-relaxed text-stone-500 md:text-base">
        {children}
      </div>
    </section>
  </Reveal>
);

export default function Privacy() {
  const { t, lang } = useLanguage();

  return (
    <main data-testid="privacy-page">
      <PageHero
        eyebrow={t("privacyEyebrow")}
        lines={[t("privacyL1"), t("privacyL2")]}
        sub={t("privacySub")}
      />

      <div className="mx-auto max-w-4xl space-y-14 px-6 pb-24 md:pb-32">
        {lang === "en" && (
          <Reveal>
            <div data-testid="privacy-en-note" className="border border-[#C89D66]/40 bg-[#C89D66]/5 p-6 md:p-8">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8F6B3C]">
                <Languages className="h-4 w-4" />
                {t("privacyEnNoteTitle")}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-stone-600">{t("privacyEnNoteText")}</p>
            </div>
          </Reveal>
        )}

        <Section num="01" title="Titolare del trattamento">
          <p>
            Il Titolare del trattamento dei dati personali è <strong className="text-stone-800">Edilcomplex2020 S.r.l.</strong> ·
            P.IVA 02558740466, con sede operativa in {CONTACTS.address}.
          </p>
          <p>
            Contatti del Titolare — Telefono:{" "}
            <a href={CONTACTS.phoneHref} className="text-[#8F6B3C] hover:underline">{CONTACTS.phone}</a>
            {" "}· Email:{" "}
            <a href={`mailto:${CONTACTS.email}`} className="text-[#8F6B3C] hover:underline">{CONTACTS.email}</a>
            {" "}· PEC:{" "}
            <a href={`mailto:${CONTACTS.pec}`} className="text-[#8F6B3C] hover:underline">{CONTACTS.pec}</a>
          </p>
        </Section>

        <Section num="02" title="Dati raccolti">
          <p>Attraverso questo sito possono essere raccolti i seguenti dati personali, forniti volontariamente dall'utente tramite il modulo di contatto:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>Nome e cognome</li>
            <li>Indirizzo email</li>
            <li>Numero di telefono</li>
            <li>Servizio di interesse e contenuto del messaggio</li>
          </ul>
          <p>
            Il sito non utilizza sistemi di profilazione né cookie pubblicitari. I dati di
            navigazione a fini statistici aggregati (Google Analytics 4, con IP anonimizzato)
            vengono raccolti solo previo consenso espresso tramite il banner cookie; sono inoltre
            presenti cookie tecnici, necessari al funzionamento del sito.
          </p>
        </Section>

        <Section num="03" title="Finalità e base giuridica">
          <p>I dati forniti sono trattati esclusivamente per:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>Rispondere alle richieste di contatto e di preventivo inviate dall'utente (art. 6, par. 1, lett. b GDPR);</li>
            <li>Adempiere ad eventuali obblighi di legge (art. 6, par. 1, lett. c GDPR).</li>
          </ul>
          <p>
            Il conferimento dei dati è facoltativo, ma il mancato conferimento dei campi
            obbligatori rende impossibile rispondere alla richiesta.
          </p>
        </Section>

        <Section num="04" title="Modalità del trattamento e servizi di terzi">
          <p>
            Il trattamento avviene con strumenti informatici e nel rispetto delle misure di
            sicurezza previste dall'art. 32 GDPR. I dati non sono oggetto di diffusione né di
            processi decisionali automatizzati.
          </p>
          <p>
            Il modulo di contatto genera un messaggio indirizzato al servizio WhatsApp (Meta
            Platforms, Inc.): cliccando "Invia su WhatsApp", i dati inseriti vengono trasferiti
            all'applicazione WhatsApp secondo le condizioni e l'informativa privacy di Meta. Il
            sito incorpora inoltre Google Maps (Google Ireland Ltd.), che può comportare il
            trattamento di dati tecnici da parte di Google secondo la propria informativa, e — solo
            previo consenso espresso — Google Analytics 4 per statistiche aggregate di navigazione
            con IP anonimizzato.
          </p>
        </Section>

        <Section num="05" title="Conservazione dei dati">
          <p>
            I dati inviati tramite il modulo di contatto sono conservati per il tempo necessario a
            gestire la richiesta e, in caso di rapporto commerciale, per i termini previsti dalla
            normativa civile e fiscale. Decorso tale periodo, i dati sono cancellati o resi
            anonimi.
          </p>
        </Section>

        <Section num="06" title="Diritti dell'interessato">
          <p>
            Ai sensi degli artt. 15–22 GDPR, l'utente ha diritto di: accedere ai propri dati,
            ottenerne la rettifica o la cancellazione, limitare od opporsi al trattamento, ottenere
            la portabilità dei dati e revocare il consenso in qualsiasi momento.
          </p>
          <p>
            Per esercitare i propri diritti è sufficiente scrivere a{" "}
            <a href={`mailto:${CONTACTS.email}`} className="text-[#8F6B3C] hover:underline">{CONTACTS.email}</a>.
            È inoltre possibile proporre reclamo al Garante per la protezione dei dati personali
            (www.garanteprivacy.it).
          </p>
        </Section>

        <Section num="07" title="Cookie Policy">
          <p>
            Questo sito utilizza esclusivamente <strong className="text-stone-800">cookie tecnici</strong>,
            necessari al funzionamento delle pagine e alla memorizzazione delle preferenze
            essenziali (ad esempio l'accettazione del banner cookie). Non vengono utilizzati cookie
            di profilazione, analytics o di terze parti a fini pubblicitari.
          </p>
          <p>
            La navigazione può essere in ogni caso gestita tramite le impostazioni del proprio
            browser, che consentono di bloccare o eliminare i cookie. Per tutti i dettagli consulta
            la{" "}
            <Link to="/cookie-policy" data-testid="privacy-cookie-link" className="text-[#8F6B3C] underline underline-offset-2 hover:text-[#7A5A30]">
              Cookie Policy completa
            </Link>.
          </p>
        </Section>

        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
            {t("lastUpdate")}
          </p>
        </Reveal>

        <Reveal>
          <Link
            data-testid="privacy-back-home"
            to="/"
            className="group inline-flex items-center gap-3 border border-stone-300 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {t("backHome")}
          </Link>
        </Reveal>
      </div>
    </main>
  );
}
